// Generic stats card (v2) — one renderer for every stat category (Passing,
// Attacking, Defending, Goalkeeping). A category from /player-metric-categories
// gives the metric rows; each player's values come from /player-stats. One column
// per player; 2+ players = compare mode (colour-coded columns + legend chips + ×
// remove — the shared drag-to-combine).
//
// Selector order is season-FIRST (C2): each column picks a SEASON across the
// player's whole career (/player-seasons?playerId), which drives the COMPETITION
// picker (/player-competitions?playerId&seasonId). The chosen competition scope
// (All / one; select-exclude pills land in C2c) lives per column in the widget's
// `state` (keyed by refKey, like state.colors) — NOT in PlayerRef identity — and
// resolves to the competition_id[] handed to /player-stats (C1 accepts ≥1 and
// sums raw across them). Default scope = the ref's single anchor competition, so
// a card's numbers don't change until the user picks All / another competition.
//
// makeStatsWidget(categoryKey) binds the renderer to one category so the registry
// can list four cards that all share this code. Goalkeeping passes an `emptyNoun`
// + `columnNote` so an outfielder (no keeper data) gets a clear message / note
// instead of a table of dashes — see the empty-data handling below.
//
// N players = N /player-stats calls (each may be a different comp/season, so they
// can't be pooled). Percentile bars, the compare-against filter engine, and per90
// are deferred (they need the population aggregate layer) — this card shows values.

import { Fragment, useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import type { PlayerSeasonsResponse, PlayerCompetitionsResponse } from "@catbento/shared";
import type { WidgetProps } from "../registry.js";
import type { PlayerRef } from "../types.js";
import { useMetricCategories } from "../../data/stats.js";
import type { StatsResponse } from "../../data/players.js";
import { apiGet, apiSend } from "../../api.js";
import { formatMetric } from "../../widgets/fullStats.js";
import {
  findCategory,
  categoryGroups,
  categoryKeys,
  rowHasData,
  effectiveSignalKeys,
} from "../statsCategory.js";
import { assignColors, colorAt } from "../compareColors.js";
import { CompetitionScopePicker } from "./CompetitionScopePicker.js";
import {
  resolveCompetitionScope,
  defaultCompetitionScope,
  type CompetitionScope,
} from "../competitionScope.js";
import { refKey } from "../playerRef.js";
import { useCanvasStore } from "../store.js";
import { theme } from "../theme.js";

const FIVE_MIN = 5 * 60 * 1000;

interface StatsWidgetOptions {
  /** Category noun for the "no data" message, e.g. "goalkeeping". When set, a card
   *  whose players all lack data in this category shows the message instead of a
   *  table of dashes. Omitted for categories every player has (passing, etc.). */
  emptyNoun?: string;
  /** Short note under an empty column's name in a mixed comparison, e.g. "no keeper
   *  data" — says WHY that one column is blank while others have numbers. */
  columnNote?: string;
  /** The keeper-specific metric keys that decide "does this player belong here".
   *  Goalkeeping needs this because an outfielder has the keeper metrics at 0 but
   *  some team-context ones (e.g. opponent_np_shots) non-zero — the gate must look
   *  ONLY at keeper-specific metrics. Omitted → every metric counts. */
  signalKeys?: string[];
}

export function makeStatsWidget(categoryKey: string, options: StatsWidgetOptions = {}) {
  function StatsWidget({ instanceId, players, state, onPlayerDragStart }: WidgetProps) {
    const catQ = useMetricCategories(false);
    const groups = useMemo(
      () => categoryGroups(findCategory(catQ.data, categoryKey)),
      [catQ.data]
    );
    const keys = useMemo(() => categoryKeys(groups), [groups]);

    // 2+ players = compare mode: colour-coded columns + a legend. Colours are keyed
    // by ref and kept stable when a player is removed — see compareColors.ts.
    const compare = players.length >= 2;
    const colors = useMemo(
      () => assignColors(players, (state.colors as Record<string, number>) ?? {}),
      [players, state.colors]
    );
    const colorFor = (p: PlayerRef): string => colorAt(colors[refKey(p)] ?? 0);

    // Season options per player — season-FIRST means the WHOLE career, not one
    // competition's seasons (competitionId omitted → every season the player
    // played). Drives the competition picker below.
    const seasonQueries = useQueries({
      queries: players.map((p) => ({
        queryKey: ["player-seasons-career", p.playerId],
        enabled: p.playerId !== null,
        staleTime: FIVE_MIN,
        queryFn: () =>
          apiGet<PlayerSeasonsResponse>(`/player-seasons?playerId=${p.playerId}`),
      })),
    });

    // Competition options per player for the CHOSEN season (the inverse lookup C1
    // added). Empty until a season is picked / the list loads.
    const competitionQueries = useQueries({
      queries: players.map((p) => ({
        queryKey: ["player-competitions-in-season", p.playerId, p.seasonId],
        enabled: p.playerId !== null && p.seasonId !== null,
        staleTime: FIVE_MIN,
        queryFn: () =>
          apiGet<PlayerCompetitionsResponse>(
            `/player-competitions?playerId=${p.playerId}&seasonId=${p.seasonId}`
          ),
      })),
    });

    // The competition scope each column is in, kept per column in `state` (keyed
    // by refKey, like state.colors). Absent → the derived default (the ref's
    // single anchor competition, or All if that comp isn't in the chosen season).
    const scopeState = (state.competitionScope as Record<string, CompetitionScope>) ?? {};
    const seasonCompIdsAt = (index: number): number[] =>
      competitionQueries[index]?.data?.competitions.map((c) => c.id) ?? [];
    const scopeAt = (index: number): CompetitionScope => {
      const p = players[index];
      if (!p) return { mode: "all" };
      return scopeState[refKey(p)] ?? defaultCompetitionScope(p.competitionId, seasonCompIdsAt(index));
    };
    const resolvedCompsAt = (index: number): number[] =>
      resolveCompetitionScope(scopeAt(index), seasonCompIdsAt(index));

    // One stats query per player. The competition scope (not the ref's single
    // comp) sets which competitions are summed — All hands every season-comp to
    // /player-stats (the C1 combined path).
    const statQueries = useQueries({
      queries: players.map((p, index) => {
        const comps = resolvedCompsAt(index);
        return {
          queryKey: ["v2-stats", categoryKey, p.playerId, p.seasonId, comps, keys],
          enabled: p.seasonId !== null && keys.length > 0 && comps.length > 0,
          retry: 1,
          queryFn: () =>
            apiSend<StatsResponse>("POST", "/player-stats", {
              keys,
              where: {
                player_id: [p.playerId],
                competition_id: comps,
                season_id: [p.seasonId],
              },
              groupBy: ["player_id"],
            }),
        };
      }),
    });

    // Season change keeps the ref's competitionId as an anchor (used as the
    // default scope) — the resolver falls back to All if it isn't in the new
    // season. Changing seasonId changes the refKey, so the old column's stored
    // scope drops and the default re-derives for the new season.
    function setSeason(index: number, seasonId: number) {
      const next: PlayerRef[] = players.map((p, i) => (i === index ? { ...p, seasonId } : p));
      useCanvasStore.getState().setWidgetPlayers(instanceId, next);
    }

    function setScope(index: number, scope: CompetitionScope) {
      const p = players[index];
      if (!p) return;
      const nextScope = { ...scopeState, [refKey(p)]: scope };
      useCanvasStore.getState().updateWidget(instanceId, {
        state: { ...state, competitionScope: nextScope },
      });
    }

    function removePlayer(index: number) {
      const next = players.filter((_, i) => i !== index);
      useCanvasStore.getState().updateWidget(instanceId, {
        players: next,
        state: { ...state, colors: assignColors(next, colors) },
      });
    }

    function seasonLabel(index: number): string | null {
      const p = players[index];
      if (!p) return null;
      const seasons = seasonQueries[index]?.data?.seasons ?? [];
      return seasons.find((s) => s.id === p.seasonId)?.name ?? null;
    }

    // Per-column data state: has this player any value in this category yet?
    // Mirror the stats query's own `enabled` (season + resolved competitions).
    const settled = statQueries.every((q, i) => {
      const p = players[i];
      const enabled =
        p?.seasonId !== null && keys.length > 0 && resolvedCompsAt(i).length > 0;
      return !enabled || !q.isLoading;
    });
    // "Does this player belong in this category" is decided from the SIGNAL keys
    // (keeper-only metrics for Goalkeeping), not every metric — an outfielder's
    // team-context metrics (e.g. opponent_np_shots) are non-zero and must not count.
    const signalKeys = useMemo(
      () => effectiveSignalKeys(options.signalKeys, keys),
      [keys]
    );
    const columnHasData = (i: number): boolean =>
      rowHasData(statQueries[i]?.data?.rows?.[0], signalKeys);
    // A column that belongs to a "no data here" player (e.g. an outfielder in a
    // Goalkeeping comparison): show dashes for EVERY row, not the raw team-context
    // numbers the engine still returns — otherwise a "no keeper data" column would
    // confusingly show real values for Opp NP Shots etc. Only categories that opt
    // in (columnNote set) gate this; others always have data.
    const columnEmpty = (i: number): boolean =>
      Boolean(options.columnNote) && settled && !columnHasData(i);

    if (players.length === 0) {
      return <div style={emptyStyle}>Search a player to begin.</div>;
    }
    if (catQ.isLoading) {
      return <div style={emptyStyle}>Loading metrics…</div>;
    }
    if (groups.length === 0) {
      return <div style={{ ...emptyStyle, color: theme.danger }}>No metrics available.</div>;
    }

    // Nobody has data in this category (e.g. only outfielders in a Goalkeeping card)
    // → one clear message instead of a card full of dashes. Only for categories that
    // opt in via emptyNoun; wait until queries settle so we don't flash it mid-load.
    if (options.emptyNoun && settled && players.every((_, i) => !columnHasData(i))) {
      const who = players.length > 1 ? "these players" : "this player";
      return (
        <div style={emptyStyle}>{`No ${options.emptyNoun} data for ${who}.`}</div>
      );
    }

    return (
      <div style={{ fontFamily: theme.fontBody, color: theme.ink, fontSize: theme.fontSize.data, overflowX: "auto" }}>
        {/* Compare-mode legend — the prototype's flat .compare-chip: a coloured
            dot + name (no pill/border), a muted season, and a faint × that
            brightens on hover (the reveal is CSS in index.html .v2-scope). */}
        {compare ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 18, marginBottom: 16 }}>
            {players.map((p, i) => (
              <span
                key={`chip-${refKey(p)}-${i}`}
                className="compare-chip"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  color: theme.ink,
                }}
              >
                <span
                  style={{ width: 11, height: 11, borderRadius: "50%", background: colorFor(p), flexShrink: 0 }}
                />
                <span
                  onPointerDown={onPlayerDragStart ? (e) => onPlayerDragStart(e, p) : undefined}
                  title="Drag onto another card to compare"
                  style={{
                    cursor: onPlayerDragStart ? "grab" : "default",
                    userSelect: "none",
                    touchAction: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {p.displayName}
                  {seasonLabel(i) ? (
                    <span style={{ fontWeight: 400, fontSize: "0.75rem", color: theme.inkFaint }}>
                      {" "}
                      ({seasonLabel(i)})
                    </span>
                  ) : null}
                </span>
                <button
                  className="compare-remove"
                  aria-label={`Remove ${p.displayName}`}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => removePlayer(i)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : null}

        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead>
            <tr>
              <th style={{ ...cellBase, textAlign: "left", color: theme.inkFaint }} />
              {players.map((p, i) => {
                const seasons = seasonQueries[i]?.data?.seasons ?? [];
                // Competition options for the chosen season + this column's scope,
                // fed to the select/exclude pill picker (C2c).
                const comps = competitionQueries[i]?.data?.competitions ?? [];
                const scope = scopeAt(i);
                // A column with no data (e.g. an outfielder here) gets a small note
                // saying why it's blank, so dashes aren't mistaken for loading/zero.
                const noData = columnEmpty(i);
                return (
                  <th key={`${p.playerId}-${i}`} style={{ ...cellBase, minWidth: 92 }}>
                    {/* The name is a drag source: drag onto another card to compare,
                        or onto empty canvas to spawn a Bio. */}
                    <div
                      onPointerDown={onPlayerDragStart ? (e) => onPlayerDragStart(e, p) : undefined}
                      title={onPlayerDragStart ? "Drag onto another card to compare" : p.displayName}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        cursor: onPlayerDragStart ? "grab" : "default",
                        userSelect: "none",
                        touchAction: "none",
                      }}
                    >
                      {compare ? (
                        <span
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            background: colorFor(p),
                            flexShrink: 0,
                          }}
                        />
                      ) : null}
                      <span
                        style={{
                          fontFamily: theme.fontBody,
                          fontSize: 13,
                          fontWeight: 600,
                          color: compare ? colorFor(p) : theme.ink,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={p.displayName}
                      >
                        {p.displayName}
                      </span>
                    </div>
                    <select
                      value={p.seasonId ?? ""}
                      onChange={(e) => setSeason(i, Number(e.target.value))}
                      style={selectStyle}
                    >
                      {seasons.length === 0 && p.seasonId !== null ? (
                        <option value={p.seasonId}>current</option>
                      ) : null}
                      {seasons.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                    <CompetitionScopePicker
                      competitions={comps}
                      scope={scope}
                      onChange={(next) => setScope(i, next)}
                    />
                    {noData ? (
                      <div style={{ color: theme.inkFaint, fontSize: 10, marginTop: 3, fontWeight: 400 }}>
                        {options.columnNote}
                      </div>
                    ) : null}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.id}>
                <tr>
                  <td
                    colSpan={players.length + 1}
                    style={{
                      ...cellBase,
                      textAlign: "left",
                      fontFamily: theme.fontBody,
                      fontSize: theme.fontSize.label,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                      color: theme.inkFaint,
                      paddingTop: 10,
                    }}
                  >
                    {g.name}
                  </td>
                </tr>
                {g.rows.map((r) => (
                  <tr key={r.key}>
                    <td
                      style={{ ...cellBase, textAlign: "left", color: theme.inkMuted }}
                      title={r.definition}
                    >
                      {r.label}
                    </td>
                    {players.map((p, i) => {
                      const q = statQueries[i];
                      const value = q?.data?.rows?.[0]?.[r.key];
                      // A "no keeper data" column shows dashes for every row (not the
                      // raw team-context numbers the engine still returns).
                      const empty = columnEmpty(i);
                      return (
                        <td
                          key={`${p.playerId}-${i}`}
                          style={{
                            ...cellBase,
                            textAlign: "right",
                            fontVariantNumeric: "tabular-nums",
                            // Compare mode colours each player's column of values in
                            // their compare colour (the prototype's .compare-value-*).
                            color: empty ? theme.inkFaint : compare ? colorFor(p) : theme.ink,
                          }}
                        >
                          {empty ? "—" : q?.isLoading ? "…" : formatMetric(value, r.outputType)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  StatsWidget.displayName = `StatsWidget(${categoryKey})`;
  return StatsWidget;
}

const cellBase: React.CSSProperties = {
  padding: "3px 8px",
  borderBottom: `1px solid ${theme.border}`,
  verticalAlign: "bottom",
};

const selectStyle: React.CSSProperties = {
  marginTop: 4,
  width: "100%",
  background: theme.field,
  color: theme.inkMuted,
  border: `1px solid ${theme.border}`,
  borderRadius: 4,
  fontSize: 10,
  padding: "1px 2px",
};

const emptyStyle: React.CSSProperties = {
  display: "grid",
  placeItems: "center",
  height: "100%",
  color: theme.inkMuted,
  fontSize: 13,
  textAlign: "center",
  padding: 12,
};
